import React, { useState, useMemo } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Collapse,
  Typography,
  Pagination,
  Chip,
  Stack,
  InputAdornment,
  Skeleton,
  Alert,
  alpha,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Search as SearchIcon,
  History as HistoryIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { useQuery } from "react-query";
import { getLogs } from "../../api/services/auditLogs";
import { QK } from "../../base/qk";
import { summarizeChanges } from "./Helpers";
import { DetailedChanges } from "./DetailedChanges";
import { TimeDisplay } from "../../components/TimeStamp/TimeDisplay";
import CustomInput from "../../components/CustomForm/CustomInput";
import CustomSelect from "../../components/FormFields/CustomSelect/CustomSelect";

export default function AuditLogTable() {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [filterModel, setFilterModel] = useState("");
  const [filterAction, setFilterAction] = useState("");
  const [expanded, setExpanded] = useState(null);

  const params = useMemo(
    () => ({
      search: search || undefined,
      model_name: filterModel || undefined,
      action: filterAction || undefined,
    }),
    [search, filterModel, filterAction]
  );

  const { data, isLoading, isError } = useQuery(
    [QK.auditLogs, page, params],
    () => getLogs({ page, pageSize, params }),
    { keepPreviousData: true }
  );

  const modelOptions = useMemo(() => {
    const arr = data?.results || data || [];
    const set = new Set(arr.map((d) => d.model_name));
    return [
      { value: "", label: "All Models" },
      ...Array.from(set).sort().map((m) => ({ value: m, label: m })),
    ];
  }, [data]);

  const actionOptions = useMemo(() => {
    const arr = data?.results || data || [];
    const set = new Set(arr.map((d) => d.action));
    return [
      { value: "", label: "All Actions" },
      ...Array.from(set).sort().map((a) => ({ value: a, label: a })),
    ];
  }, [data]);

  const handleToggleExpand = (rowId) => {
    setExpanded(expanded === rowId ? null : rowId);
  };

  const rows = data?.results ?? data ?? [];
  const totalCount = data?.count || 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  if (isLoading) {
    return (
      <Box sx={{ p: 3, maxWidth: 1400, mx: "auto" }}>
        <Paper sx={{ borderRadius: "12px", border: "1px solid #E2E8F0", p: 2.5 }}>
          <Skeleton height={60} sx={{ mb: 2 }} />
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} height={70} sx={{ mb: 1 }} />
          ))}
        </Paper>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 3, maxWidth: 1400, mx: "auto" }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          Failed to load audit logs. Please try again later.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: "auto" }}>
      {/* PAGE HEADER */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "10px",
              bgcolor: alpha("#2469F0", 0.08),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <HistoryIcon sx={{ color: "#2469F0", fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ color: "#0f172a" }}>
              Audit Logs
            </Typography>
            <Typography
              variant="caption"
              color="#64748b"
              sx={{ fontSize: "0.75rem" }}
            >
              Keep track of all system activities and changes
            </Typography>
          </Box>
        </Stack>
        <Chip
          label={`${totalCount} log${totalCount !== 1 ? "s" : ""}`}
          size="small"
          sx={{
            height: 22,
            fontSize: "0.65rem",
            fontWeight: 600,
            bgcolor: alpha("#2469F0", 0.08),
            color: "#2469F0",
          }}
        />
      </Box>

      {/* FILTERS */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          borderRadius: "12px",
          border: "1px solid #E2E8F0",
          boxShadow: "none",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "center" }}
          flexWrap="wrap"
          useFlexGap
        >
          <Box sx={{ flex: "1 1 300px", minWidth: "220px" }}>
            <CustomInput
              placeholder="Search models, actions, or users..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: "#94A3B8" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box sx={{ flex: "0 1 180px", minWidth: "160px" }}>
            <CustomSelect
              options={modelOptions}
              value={filterModel}
              onChange={(e) => setFilterModel(e.target.value)}
              placeholder="All Models"
              fullWidth
            />
          </Box>

          <Box sx={{ flex: "0 1 180px", minWidth: "160px" }}>
            <CustomSelect
              options={actionOptions}
              value={filterAction}
              onChange={(e) => setFilterAction(e.target.value)}
              placeholder="All Actions"
              fullWidth
            />
          </Box>
        </Stack>
      </Paper>

      {/* TABLE */}
      <Paper
        sx={{
          borderRadius: "12px",
          border: "1px solid #E2E8F0",
          boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
          overflow: "hidden",
        }}
      >
        <Table>
          <TableHead sx={{ bgcolor: "#F8FAFC" }}>
            <TableRow>
              {[
                "Time",
                "Client",
                "Record Type",
                "Action",
                "Changed By",
                "Changes",
                "",
              ].map((label) => (
                <TableCell
                  key={label}
                  sx={{
                    fontWeight: 600,
                    color: "#475467",
                    fontSize: "0.7rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.3px",
                    py: 1.5,
                    px: 1.5,
                  }}
                >
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((row) => {
              const short = summarizeChanges(row.data);
              const isOpen = expanded === row.id;
              const clientName =
                row.client?.first_name && row.client?.last_name
                  ? `${row.client.first_name} ${row.client.last_name}`
                  : "-";

              return (
                <React.Fragment key={row.id}>
                  <TableRow
                    hover
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                      bgcolor: isOpen ? alpha("#2469F0", 0.02) : "inherit",
                      transition: "background-color 0.15s ease",
                      "&:hover": { bgcolor: alpha("#2469F0", 0.02) },
                    }}
                  >
                    <TableCell sx={{ width: 160, py: 1.5, px: 1.5 }}>
                      <TimeDisplay timestamp={row.timestamp} />
                    </TableCell>
                    <TableCell
                      sx={{ py: 1.5, px: 1.5, fontSize: "0.78rem", color: "#1A1A2E" }}
                    >
                      {clientName}
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 1.5,
                        px: 1.5,
                        fontWeight: 600,
                        fontSize: "0.78rem",
                        color: "#1A1A2E",
                      }}
                    >
                      {row.model_name}
                    </TableCell>
                    <TableCell sx={{ py: 1.5, px: 1.5 }}>
                      <Chip
                        label={row.action}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: "0.65rem",
                          fontWeight: 600,
                          bgcolor:
                            row.action === "CREATE"
                              ? alpha("#22C55E", 0.1)
                              : row.action === "UPDATE"
                              ? alpha("#2469F0", 0.1)
                              : row.action === "DELETE"
                              ? alpha("#EF4444", 0.1)
                              : alpha("#94A3B8", 0.1),
                          color:
                            row.action === "CREATE"
                              ? "#22C55E"
                              : row.action === "UPDATE"
                              ? "#2469F0"
                              : row.action === "DELETE"
                              ? "#EF4444"
                              : "#64748B",
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 1.5, px: 1.5 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <PersonIcon sx={{ fontSize: 16, color: "#94A3B8" }} />
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "0.78rem", color: "#1A1A2E" }}
                        >
                          {row?.changed_by?.full_name ??
                            row.changed_by?.username ??
                            "-"}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 400, py: 1.5, px: 1.5 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#64748B",
                          fontSize: "0.78rem",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {short}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.5, px: 1.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleToggleExpand(row.id)}
                        sx={{
                          color: "#667085",
                          p: 0.5,
                          transform: isOpen ? "rotate(180deg)" : "none",
                          transition: "transform 0.2s",
                          "&:hover": {
                            color: "#2469F0",
                            bgcolor: alpha("#2469F0", 0.06),
                          },
                        }}
                      >
                        {isOpen ? (
                          <ExpandLessIcon sx={{ fontSize: 16 }} />
                        ) : (
                          <ExpandMoreIcon sx={{ fontSize: 16 }} />
                        )}
                      </IconButton>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell style={{ padding: 0 }} colSpan={7}>
                      <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        <Box
                          sx={{
                            p: 2.5,
                            bgcolor: "#F8FAFC",
                            borderTop: "1px solid #E2E8F0",
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ mb: 2 }}
                          >
                            <Box
                              sx={{
                                width: 24,
                                height: 24,
                                borderRadius: "6px",
                                bgcolor: alpha("#2469F0", 0.08),
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <HistoryIcon
                                sx={{ fontSize: 14, color: "#2469F0" }}
                              />
                            </Box>
                            <Typography
                              variant="caption"
                              fontWeight={700}
                              color="#475467"
                              sx={{
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                              }}
                            >
                              Detailed Changes
                            </Typography>
                          </Stack>
                          <DetailedChanges data={row.data} />
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>

        {/* EMPTY STATE */}
        {rows.length === 0 && (
          <Box sx={{ p: 6, textAlign: "center" }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "12px",
                bgcolor: alpha("#94A3B8", 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 1.5,
              }}
            >
              <HistoryIcon sx={{ fontSize: 24, color: "#94A3B8" }} />
            </Box>
            <Typography
              variant="body2"
              color="#475467"
              fontWeight={500}
              sx={{ fontSize: "0.85rem" }}
            >
              No audit logs found
            </Typography>
            <Typography
              variant="caption"
              color="#94A3B8"
              sx={{ fontSize: "0.7rem" }}
            >
              Try adjusting your search or filters
            </Typography>
          </Box>
        )}

        {/* PAGINATION */}
        {totalCount > 0 && rows.length > 0 && (
          <Box sx={{ p: 2, borderTop: "1px solid #E2E8F0" }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems="center"
              spacing={2}
            >
              <Typography
                variant="caption"
                color="#64748B"
                sx={{ fontSize: "0.72rem" }}
              >
                Showing {rows.length} of {totalCount} records
              </Typography>
              {totalPages > 1 && (
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, v) => setPage(v)}
                  color="primary"
                  shape="rounded"
                  size="small"
                  sx={{
                    "& .MuiPaginationItem-root": {
                      fontSize: "0.8rem",
                      fontWeight: 500,
                    },
                  }}
                />
              )}
            </Stack>
          </Box>
        )}
      </Paper>
    </Box>
  );
}